import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import QuizOverlay from './QuizOverlay';
import QuizCountdown from './QuizCountdown';
import AudioControls from './AudioControls';

// Hooks
import { useSnakeAudio } from '../../hooks/useSnakeAudio';
import { usePreventPullToRefresh } from '../../hooks/usePreventPullToRefresh';
import { useParentWrapperSwipe } from '../../hooks/useParentWrapperSwipe';

// Utils
import * as urls from '../../utils/urls';

// Styles
import styles from './style.module.scss';

interface Position {
  x: number;
  y: number;
}

type Answer = { id: number; answer: string; is_correct: boolean };
type Question = {
  id: number;
  body: string;
  type: string;
  answers?: Answer[];
  correct_answer?: string;
};

type QuizData = {
  question: string;
  options: string[];
  correctAnswer: number;
  questionId?: number;
};

type SnakeGameProps = {
  quizId: number;
  title: string;
  questions: Question[];
};

const getCsrfToken = (): string => {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return token ?? '';
};

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SPEED = 200;
const QUIZ_INTERVAL = 20000;
const SPEED_INCREMENT = 15;
const MIN_SPEED = 50;

const QUESTIONS_POLL_INTERVAL_MS = 2000;
const QUESTIONS_GENERATION_TIMEOUT_MS = 60000;
const QUESTIONS_GENERATION_DEBOUNCE_MS = 5000;
const QUESTIONS_TRIGGER_THRESHOLD = 4;

const SnakeGame: React.FC<SnakeGameProps> = ({ quizId, title, questions: initialQuestions }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    playEatSound,
    playCorrectSound,
    playWrongSound,
    playGameOverSound,
    toggleMute,
    isMuted,
    sfxVolume,
    setSfxVolume,
  } = useSnakeAudio();

  usePreventPullToRefresh();

  const [snake, setSnake] = useState<Position[]>([{ x: 10, y: 10 }]);
  const [food, setFood] = useState<Position>({ x: 15, y: 15 });
  const [direction, setDirection] = useState<Position>({ x: 1, y: 0 });
  const lastAppliedDirection = useRef<Position>({ x: 1, y: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [hearts, setHearts] = useState(3);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [survivalTime, setSurvivalTime] = useState(0);
  const [timeUntilNextQuiz, setTimeUntilNextQuiz] = useState(QUIZ_INTERVAL);
  const [currentQuiz, setCurrentQuiz] = useState<QuizData | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const lastQuizTime = useRef(Date.now());
  const gameStartTime = useRef(Date.now());
  const gameLoopRef = useRef<number | undefined>(undefined);
  const countdownRef = useRef<number | undefined>(undefined);
  const [sessionId] = useState<string>(() => crypto.randomUUID());
  const [startTime] = useState<number>(() => Date.now());
  const [finished, setFinished] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalQuestions = questions.length;
  
  const currentQuestion = useMemo(() => {
    if (totalQuestions === 0) return undefined;
    return questions[questionIndex] ?? undefined;
  }, [questions, questionIndex, totalQuestions]);

  const resultsPath = useMemo(() => {
    if (!sessionId) return null;
    return `/quizzes/${quizId}/results?session_id=${sessionId}&start_time=${startTime}`;
  }, [quizId, sessionId, startTime]);

  const generateFood = useCallback(() => {
    const newFood = {
      x: Math.floor(Math.random() * GRID_SIZE),
      y: Math.floor(Math.random() * GRID_SIZE),
    };
    setFood(newFood);
  }, []);

  const finishQuiz = useCallback(() => {
    if (finished || !resultsPath) return;

    setFinished(true);
    setGameOver(true);

    const turbo = (window as typeof window & { Turbo?: { visit?: (path: string, options?: Record<string, unknown>) => void } }).Turbo;
    if (turbo?.visit) {
      turbo.visit(resultsPath, { action: 'replace' });
      return;
    }

    window.location.href = resultsPath;
  }, [finished, resultsPath]);

  const prepareQuizFromQuestion = useCallback((question: Question): QuizData | null => {
    if (!question.answers || question.answers.length === 0) {
      return null;
    }

    const options = question.answers.map(a => a.answer);
    const correctIndex = question.answers.findIndex(a => a.is_correct);

    if (correctIndex === -1) return null;

    return {
      question: question.body,
      options,
      correctAnswer: correctIndex,
      questionId: question.id,
    };
  }, []);

  const checkQuizTrigger = useCallback(() => {
    const now = Date.now();
    const timeSinceLastQuiz = now - lastQuizTime.current;
    
    if (timeSinceLastQuiz >= QUIZ_INTERVAL && !isPaused && !gameOver && questionIndex < totalQuestions) {
      const quiz = prepareQuizFromQuestion(currentQuestion!);
      if (quiz) {
        setCurrentQuiz(quiz);
        setIsPaused(true);
      }
    }
  }, [isPaused, gameOver, questionIndex, totalQuestions, currentQuestion, prepareQuizFromQuestion]);

  const submitQuestionResult = useCallback(
    async (question: Question | undefined, answerValue: string, isCorrect: boolean) => {
      if (!question || !sessionId) return;

      try {
        const response = await fetch(urls.questionResultUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken()
          },
          body: JSON.stringify({
            question_result: {
              question_id: question.id,
              answer: answerValue,
              correct: isCorrect
            },
            session_id: sessionId
          })
        });
      } catch (error) {
        console.error('Error submitting question result', error);
      }
    },
    [sessionId]
  );

  const cleanupPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsGeneratingQuestions(false);
  }, []);

  const generateMoreQuestions = useCallback(async () => {
    if (isGeneratingQuestions) {
      return;
    }

    setIsGeneratingQuestions(true);
    const currentQuestionCount = questions.length;
    
    try {
      const response = await fetch(urls.aiGameGenerateMoreQuestionsUrl(quizId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        }
      });

      if (!response.ok) {
        console.error('Failed to trigger question generation');
        setIsGeneratingQuestions(false);
        return;
      }

      pollIntervalRef.current = setInterval(async () => {
        try {
          const questionsResponse = await fetch(urls.aiGameQuestionsUrl(quizId), {
            headers: {
              'X-CSRF-Token': getCsrfToken()
            }
          });

          if (!questionsResponse.ok) {
            return;
          }

          const data = await questionsResponse.json();
          const newQuestions = data.questions || [];
          
          if (newQuestions.length > currentQuestionCount) {
            setQuestions(newQuestions);
            cleanupPolling();
          }
        } catch (error) {
          console.error('Error polling for questions', error);
          cleanupPolling();
        }
      }, QUESTIONS_POLL_INTERVAL_MS);

      pollTimeoutRef.current = setTimeout(() => {
        cleanupPolling();
      }, QUESTIONS_GENERATION_TIMEOUT_MS);
    } catch (error) {
      console.error('Error initiating question generation', error);
      setIsGeneratingQuestions(false);
    }
  }, [quizId, questions.length, isGeneratingQuestions, cleanupPolling]);

  const shouldTriggerGeneration = useCallback((currentIndex: number, currentTotal: number): boolean => {
    return currentIndex === currentTotal - QUESTIONS_TRIGGER_THRESHOLD && currentTotal > QUESTIONS_TRIGGER_THRESHOLD;
  }, []);

  const handleQuizComplete = useCallback((correct: boolean, selectedIndex: number) => {
    setIsPaused(false);
    setQuestionsAnswered(prev => prev + 1);    
    lastQuizTime.current = Date.now();
    setTimeUntilNextQuiz(QUIZ_INTERVAL);
    setSpeed(prev => Math.max(prev - SPEED_INCREMENT, MIN_SPEED));
    
    if (correct) {
      playCorrectSound();
      setScore(prev => prev + 50);
      setCorrectAnswers(prev => prev + 1);
    } else {
      playWrongSound();
      setHearts(prev => {
        const newHearts = prev - 1;
        if (newHearts <= 0) {
          playGameOverSound();
          setTimeout(() => {
            finishQuiz();
          }, 500);
        }
        return newHearts;
      });
    }

    if (currentQuestion && currentQuiz) {
      const selectedAnswer = currentQuestion.answers?.[selectedIndex];
      if (selectedAnswer) {
        submitQuestionResult(currentQuestion, selectedAnswer.answer, correct);
      }
    }

    setCurrentQuiz(null);
    const newIndex = questionIndex + 1;
    setQuestionIndex(newIndex);

    if (shouldTriggerGeneration(newIndex, totalQuestions) && !isGeneratingQuestions) {
      const timeSinceLastGeneration = Date.now() - lastGeneratedAt;
      if (timeSinceLastGeneration > QUESTIONS_GENERATION_DEBOUNCE_MS) {
        setLastGeneratedAt(Date.now());
        generateMoreQuestions();
      }
    }
  }, [currentQuestion, currentQuiz, submitQuestionResult, playCorrectSound, playWrongSound, playGameOverSound, finishQuiz, questionIndex, totalQuestions, shouldTriggerGeneration, isGeneratingQuestions, lastGeneratedAt, generateMoreQuestions]);

  const moveSnake = useCallback(() => {
    if (gameOver || isPaused) return;
    lastAppliedDirection.current = direction;

    setSnake(prevSnake => {
      const newHead = {
        x: (prevSnake[0].x + direction.x + GRID_SIZE) % GRID_SIZE,
        y: (prevSnake[0].y + direction.y + GRID_SIZE) % GRID_SIZE,
      };

      if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
        playGameOverSound();
        setTimeout(() => {
          finishQuiz();
        }, 500);
        return prevSnake;
      }

      const newSnake = [newHead, ...prevSnake];

      if (newHead.x === food.x && newHead.y === food.y) {
        playEatSound();
        setScore(prev => prev + 10);
        generateFood();
      } else {
        newSnake.pop();
      }

      return newSnake;
    });

    checkQuizTrigger();
  }, [direction, food, gameOver, isPaused, generateFood, checkQuizTrigger, playEatSound, playGameOverSound, finishQuiz]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (gameOver) return;

      const lastDir = lastAppliedDirection.current;

      switch (event.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          if (lastDir.y === 0) setDirection({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          if (lastDir.y === 0) setDirection({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (lastDir.x === 0) setDirection({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (lastDir.x === 0) setDirection({ x: 1, y: 0 });
          break;
      }
    },
    [gameOver]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    gameLoopRef.current = window.setInterval(moveSnake, speed);
    return () => {
      if (gameLoopRef.current) clearInterval(gameLoopRef.current);
    };
  }, [moveSnake, speed]);

  useEffect(() => {
    if (gameOver || isPaused || questionIndex >= totalQuestions) return;

    countdownRef.current = window.setInterval(() => {
      const now = Date.now();
      const timeSinceLastQuiz = now - lastQuizTime.current;
      const remaining = QUIZ_INTERVAL - timeSinceLastQuiz;
      setTimeUntilNextQuiz(Math.max(0, remaining));
    }, 100);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [gameOver, isPaused, questionIndex, totalQuestions]);

  useEffect(() => {
    setQuestions(initialQuestions);
  }, [initialQuestions]);

  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, [cleanupPolling]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#00FF00';
    ctx.globalAlpha = 0.2;
    for (let i = 0; i <= GRID_SIZE; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_SIZE, 0);
      ctx.lineTo(i * CELL_SIZE, GRID_SIZE * CELL_SIZE);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_SIZE);
      ctx.lineTo(GRID_SIZE * CELL_SIZE, i * CELL_SIZE);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    snake.forEach((segment, index) => {
      ctx.fillStyle = index === 0 ? '#00FF00' : '#00DD00';
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#00FF00';
      ctx.fillRect(segment.x * CELL_SIZE, segment.y * CELL_SIZE, CELL_SIZE - 2, CELL_SIZE - 2);
    });

    ctx.fillStyle = '#00FFFF';
    ctx.shadowBlur = 15;
    ctx.shadowColor = '#00FFFF';
    ctx.fillRect(food.x * CELL_SIZE, food.y * CELL_SIZE, CELL_SIZE - 2, CELL_SIZE - 2);
    ctx.shadowBlur = 0;
  }, [snake, food]);


  const handleSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      const lastDir = lastAppliedDirection.current;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX > absY) {
        if (lastDir.x === 0) {
          setDirection(deltaX > 0 ? { x: 1, y: 0 } : { x: -1, y: 0 });
        }
      } else {
        if (lastDir.y === 0) {
          setDirection(deltaY > 0 ? { x: 0, y: 1 } : { x: 0, y: -1 });
        }
      }
    },
    []
  );

  useParentWrapperSwipe({
    containerRef,
    onSwipe: handleSwipe,
    enabled: !gameOver && !isPaused,
  });

  // const handleDirectionButton = useCallback(
  //   (newDirection: Position) => {
  //     if (gameOver) return;
      
  //     const lastDir = lastAppliedDirection.current;
      
  //     if (newDirection.x !== 0 && lastDir.x === 0) {
  //       setDirection(newDirection);
  //     } else if (newDirection.y !== 0 && lastDir.y === 0) {
  //       setDirection(newDirection);
  //     }
  //   },
  //   [gameOver]
  // );

  return (
    <div 
      ref={containerRef}
      className={`${styles.container} flex flex-col items-center w-full max-w-[400px] mx-auto`}
      style={{ overscrollBehaviorY: 'none' }}
    >
      <div className={styles.gameWrapper}>
        <div className={styles.stats}>
          <div className={styles.scoreText}>
            <span>{t('games.snake.score').toUpperCase()}:</span>
            <span>{score}</span>
          </div>
          <div className={styles.hearts}>
            {Array.from({ length: 3 }).map((_, index) => (
              <span
                key={index}
                className={`${styles.heart} ${index < hearts ? styles.heartActive : styles.heartInactive}`}
              >
                ♥
              </span>
            ))}
          </div>
          <div className={styles.lengthText}>
            <span>{t('games.snake.length').toUpperCase()}:</span>
            <span>{snake.length}</span>
          </div>
          <AudioControls
            isMuted={isMuted}
            sfxVolume={sfxVolume}
            onToggleMute={toggleMute}
            onSfxVolumeChange={setSfxVolume}
          />
        </div>

        <div 
          className={styles.canvasWrapper}
        >
          <canvas
            ref={canvasRef}
            width={GRID_SIZE * CELL_SIZE}
            height={GRID_SIZE * CELL_SIZE}
            className={styles.canvas}
          />
        </div>

        {questionIndex < totalQuestions && (
          <div className={styles.countdown}>
            <QuizCountdown timeUntilNextQuiz={timeUntilNextQuiz} />
          </div>
        )}
      </div>

      <div className={`${styles.controlsText} hidden md:block`}>
        {t('games.snake.arrow_keys_or_wasd_to_move')}
      </div>
      <div className={`${styles.controlsText} block md:hidden`}>
        {t('games.snake.swipe_to_move')}
      </div>

      {isPaused && currentQuiz && (
        <div className={styles.overlayFullscreen}>
          <QuizOverlay 
            quizData={currentQuiz}
            onComplete={handleQuizComplete}
          />
        </div>
      )}

      {/* <div className={styles.mobileControls}>
        <button
          className={styles.controlButton}
          onClick={() => handleDirectionButton({ x: 0, y: -1 })}
          disabled={gameOver || isPaused}
          aria-label="Up"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12H8V20H16V12H20L12 4Z" fill="currentColor"/>
          </svg>
        </button>
        <div className={styles.controlRow}>
          <button
            className={styles.controlButton}
            onClick={() => handleDirectionButton({ x: -1, y: 0 })}
            disabled={gameOver || isPaused}
            aria-label="Left"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 12L12 4V8H20V16H12V20L4 12Z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className={styles.controlButton}
            onClick={() => handleDirectionButton({ x: 0, y: 1 })}
            disabled={gameOver || isPaused}
            aria-label="Down"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20L20 12H16V4H8V12H4L12 20Z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className={styles.controlButton}
            onClick={() => handleDirectionButton({ x: 1, y: 0 })}
            disabled={gameOver || isPaused}
            aria-label="Right"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 12L12 20V16H4V8H12V4L20 12Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default SnakeGame;

